import { defineStore } from "pinia";

import api from "@/lib/api";
import { WORKSPACE_EXAMS, getWorkspaceExam } from "@/config/workspace";

// 나의 검사 관리 업무 화면 공통 상태: 선택한 시험(GNB 2단) · 시험 구분 · 회차(LNB 시험 카드) · LNB 접기
// 구분·회차를 바꾸면 오른쪽 콘텐츠 전체가 해당 회차 기준으로 바뀐다 (roundId === "all" 이면 구분 내 전체 회차)

const STORAGE_KEY = "ws-state";
// 새로 로그인한 뒤 처음 회차 목록을 받으면 시험 중인 회차로 맞추기 위한 표시
const FRESH_LOGIN_KEY = "ws-fresh-login";

// 로그인 직후 호출 (SSO 콜백 · 개발용 로그인)
export const markWorkspaceFreshLogin = () => {
  try {
    localStorage.setItem(FRESH_LOGIN_KEY, "1");
  } catch {
    // 저장 불가여도 이전 선택을 유지할 뿐 화면 동작에는 영향 없음
  }
};
const isFreshLogin = () => {
  try {
    return localStorage.getItem(FRESH_LOGIN_KEY) === "1";
  } catch {
    return false;
  }
};
const clearFreshLogin = () => {
  try {
    localStorage.removeItem(FRESH_LOGIN_KEY);
  } catch {
    // 무시
  }
};

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

// 서버가 'YYYY-MM-DD' 문자열로 내려주므로 로컬 자정 기준 Date 로 바꿔 일 단위로 비교한다
const toDate = (text) => {
  const [y, m, d] = String(text).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};
const diffDays = (text) => Math.round((toDate(text) - today()) / 86400000);
const md = (text) => String(text).slice(5).replace("-", ".");

const PHASES = [
  { key: "recept", name: "접수", live: "접수 중", start: "recept_start_date", end: "recept_end_date" },
  { key: "exam", name: "시험", live: "시험 기간", start: "exam_start_date", end: "exam_end_date" },
  { key: "score", name: "채점", live: "채점 기간", start: "score_start_date", end: "score_end_date" },
  { key: "result", name: "성적발표", live: "성적발표 기간", start: "result_start_date", end: "result_end_date" },
];

// 회차의 현재 단계: 진행 중인 단계 → 다음에 시작할 단계 → 종료 순으로 판정 (날짜 없는 단계는 건너뜀)
export const getRoundPhase = (round) => {
  if (!round) return null;
  const t = today();
  const phases = PHASES.filter((p) => round[p.start] && round[p.end]);

  const live = phases.find((p) => toDate(round[p.start]) <= t && t <= toDate(round[p.end]));
  if (live) {
    return {
      key: live.key,
      live: true,
      label: live.live,
      range: `${md(round[live.start])} ~ ${md(round[live.end])}`,
      dday: `${live.key === "recept" ? "마감" : "종료"} D-${diffDays(round[live.end])}`,
    };
  }

  const next = phases.find((p) => t < toDate(round[p.start]));
  if (next) {
    return {
      key: next.key,
      live: false,
      label: `${next.name} 시작 전`,
      range: `${next.name} ${md(round[next.start])} ~ ${md(round[next.end])}`,
      dday: `D-${diffDays(round[next.start])}`,
    };
  }

  const last = phases[phases.length - 1];
  return {
    key: "end",
    live: false,
    label: "종료",
    range: last ? `${last.name} ${md(round[last.end])} 마감` : "",
    dday: "",
  };
};

// 기간과 오늘 사이 거리(일): 기간 중이면 0, 시작 전이면 시작까지, 끝났으면 끝난 뒤로 지난 일수
const distanceToPeriod = (start, end) => {
  const t = today();
  const s = toDate(start);
  const e = toDate(end || start);
  if (s <= t && t <= e) return 0;
  return Math.round((t < s ? s - t : t - e) / 86400000);
};

// 일정이 오늘과 가장 가까운 회차 (같은 거리면 앞으로 다가오는 쪽 → 시작일이 빠른 쪽)
const pickNearest = (rounds, startKey, endKey) =>
  rounds
    .filter((r) => r[startKey])
    .map((r) => ({ r, d: distanceToPeriod(r[startKey], r[endKey]), upcoming: toDate(r[startKey]) >= today() }))
    .sort((a, b) => a.d - b.d || Number(b.upcoming) - Number(a.upcoming) || toDate(a.r[startKey]) - toDate(b.r[startKey]))[0]?.r ?? null;

// 처음 들어올 때 고르는 회차: 시험 기간 중인 회차 → 없으면 시험 일정이 오늘과 가장 가까운 회차
const pickExamRound = (rounds) => pickNearest(rounds, "exam_start_date", "exam_end_date");

const SE_LABEL = { SEM1: "1학기", SEM2: "2학기" };

// 회차 표시명 — 예) 2026년 2학기(1회차), 2026년 1회차 / 비공개 맞춤 검사는 검사명
export const getRoundLabel = (round) => {
  if (!round) return "";
  if (round.exam_se === "CUSTOM") return round.exam_name;
  const se = SE_LABEL[round.exam_se];
  return se
    ? `${round.exam_year}년 ${se}(${round.round}회차)`
    : `${round.exam_year}년 ${round.round}회차`;
};

export const useWorkspaceStore = defineStore("workspace", {
  state: () => {
    const saved = loadSaved();
    return {
      examType: saved.examType || "",
      // 시험별로 마지막에 고른 구분 { MLSRD: "STANDARD", ... }
      categoryByType: saved.categoryByType || {},
      // 시험·구분별로 마지막에 고른 회차 { "MLSRD:STANDARD": "MLSRD_2026_1" | "all", ... }
      roundByScope: saved.roundByScope || {},
      rail: !!saved.rail,
      rounds: [],
      loaded: false,
      loading: false,
      // 선택 회차의 내 채점 배정 (/score/exam/user-assignment) — 채점 관리 하위 메뉴 노출 판정용
      gradingAssignments: [],
      gradingAssignmentsExamId: "",
      // 제목 카드 단계 탭(#ws-head-tabs)을 지금 채우고 있는 화면 { grading: true, plan: false }
      headTabSources: {},
      // 화면별 회차 제한 (예: 시험 계획 = 접수 가능한 시험만). null 이면 전체 회차
      //  { key: "plan", ids: ["ESSAY_2026_1_SEM2", ...], emptyLabel: "시험 없음" }
      roundFilter: null,
      // 목록 → 상세 화면 제목 카드 (시안: [‹ 목록] 채점 기준 설정 · M2-KOR-0401). null 이면 메뉴 기준 제목
      //  { title, description, crumb, onBack } — 상세에서는 단계 탭 · 시험 선택 줄을 숨긴다
      headDetail: null,
      // 콘텐츠 크게 보기 (시안 pv-zoom): 헤더 · LNB 를 접고 본문 여백을 줄인다 (모든 업무 화면 제목 카드에서 켜고 끔)
      contentZoom: false,
    };
  },

  getters: {
    // 학교에 노출되는 회차가 있는 시험만 GNB 2단에 표시 (회차 정보가 없으면 3종 모두)
    assignedExams(state) {
      const types = new Set(state.rounds.map((r) => r.exam_type));
      const list = WORKSPACE_EXAMS.filter((e) => types.has(e.type));
      return list.length ? list : WORKSPACE_EXAMS;
    },
    categories(state) {
      return getWorkspaceExam(state.examType).categories;
    },
    category() {
      const key = this.categoryByType[this.examType];
      return this.categories.find((c) => c.key === key) ?? this.categories[0];
    },
    scopeKey() {
      return `${this.examType}:${this.category.key}`;
    },
    // 선택한 시험 · 구분의 회차 (화면별 회차 제한이 있으면 그 안에서만)
    roundsOfExam(state) {
      const se = this.category.se;
      const allowed = state.roundFilter ? new Set(state.roundFilter.ids) : null;
      return state.rounds.filter(
        (r) =>
          r.exam_type === state.examType &&
          se.includes(r.exam_se) &&
          (!allowed || allowed.has(r.exam_id)),
      );
    },
    // 회차 전체 선택 허용 여부 (회차 제한 화면은 회차 하나를 골라야 함)
    allowAllRound(state) {
      return !state.roundFilter;
    },
    roundId() {
      const id = this.roundByScope[this.scopeKey] || "all";
      // 회차 제한 화면에서는 목록 밖 회차·전체를 선택값으로 보지 않는다
      if (this.roundFilter && !this.roundsOfExam.some((r) => r.exam_id === id)) {
        return this.roundsOfExam[0]?.exam_id || "all";
      }
      return id;
    },
    // 제목 카드 하단 줄에 단계 탭이 있는지 (있으면 탭 · 시험 선택 배치)
    hasHeadTabs(state) {
      return Object.values(state.headTabSources).some(Boolean);
    },
    // 선택 회차에서 한 과목이라도 채점 팀장인지 (채점 모니터링 메뉴 노출)
    isGradingLeader(state) {
      return state.gradingAssignments.some((item) => item.is_leader === "Y");
    },
    // 선택 회차 (전체면 null)
    currentRound() {
      if (this.roundId === "all") return null;
      return this.roundsOfExam.find((r) => r.exam_id === this.roundId) ?? null;
    },
  },

  actions: {
    persist() {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            examType: this.examType,
            categoryByType: this.categoryByType,
            roundByScope: this.roundByScope,
            rail: this.rail,
          }),
        );
      } catch {
        // 저장 불가(사생활 보호 모드 등)여도 화면 동작에는 영향 없음
      }
    },

    async fetchRounds(force = false) {
      // 새로 로그인했으면 이전 사용자 세션에서 받아 둔 목록이 있어도 다시 받는다
      const freshLogin = isFreshLogin();
      if ((this.loaded && !force && !freshLogin) || this.loading) return;
      this.loading = true;
      try {
        const { data } = await api.post("/exam/workspace/rounds", {});
        this.rounds = data?.success ? data.result?.list ?? [] : [];
      } catch (error) {
        console.error("회차 목록 조회 실패:", error);
        this.rounds = [];
      } finally {
        this.loading = false;
        this.loaded = true;
      }
      this.ensureSelection();
      // 로그인 후 처음 들어왔을 때: 노출되는 시험 전체에서 시험 중인(없으면 시험 일정이 가장 가까운) 회차를 미리 선택
      if (freshLogin && this.rounds.length) {
        const visible = new Set(this.assignedExams.map((e) => e.type));
        const round = pickExamRound(this.rounds.filter((r) => visible.has(r.exam_type)));
        if (round) this.selectRound(round);
        clearFreshLogin();
      }
    },

    // 저장된 선택값이 현재 목록에 없으면 기본값으로 맞춘다
    ensureSelection() {
      if (!this.assignedExams.some((e) => e.type === this.examType)) {
        this.examType = this.assignedExams[0].type;
      }
      const id = this.roundByScope[this.scopeKey];
      const exists = id === "all" || this.roundsOfExam.some((r) => r.exam_id === id);
      // 회차 목록을 못 받은 경우(조회 실패 등)엔 저장된 선택을 건드리지 않는다
      if (!exists && this.roundsOfExam.length) {
        this.roundByScope = { ...this.roundByScope, [this.scopeKey]: this.defaultRoundId() };
      }
      this.persist();
    },

    // 기본 회차(이 시험·구분을 처음 볼 때): 시험 중인 회차 → 시험 일정이 가장 가까운 회차 → 가장 최근 회차
    defaultRoundId() {
      return (pickExamRound(this.roundsOfExam) ?? this.roundsOfExam[0])?.exam_id ?? "all";
    },

    setExamType(type) {
      if (type === this.examType) return;
      this.examType = type;
      this.ensureSelection();
    },

    // 선택 회차의 내 채점 배정 조회 (서논술형만). 같은 회차면 다시 부르지 않는다
    async fetchGradingAssignments() {
      const examId = this.examType === "ESSAY" ? this.currentRound?.exam_id || "" : "";
      if (examId === this.gradingAssignmentsExamId) return;
      this.gradingAssignmentsExamId = examId;
      if (!examId) {
        this.gradingAssignments = [];
        return;
      }
      try {
        const { data } = await api.post("/score/exam/user-assignment", { exam_id: examId });
        if (this.gradingAssignmentsExamId !== examId) return;
        this.gradingAssignments = data?.result?.scoreUserList ?? [];
      } catch (error) {
        console.error("채점 배정 조회 실패:", error);
        if (this.gradingAssignmentsExamId === examId) this.gradingAssignments = [];
      }
    },

    // 화면별 회차 제한 설정/해제 (화면 진입 시 설정, 떠날 때 null)
    setRoundFilter(filter) {
      this.roundFilter = filter;
    },

    // 화면이 제목 카드 단계 탭을 쓰는지 알린다 (Teleport #ws-head-tabs 와 함께 사용)
    setHeadTabs(key, on) {
      if (!!this.headTabSources[key] === !!on) return;
      this.headTabSources = { ...this.headTabSources, [key]: !!on };
    },

    setHeadDetail(detail) {
      this.headDetail = detail || null;
    },

    setContentZoom(on) {
      this.contentZoom = !!on;
    },

    clearGradingAssignments() {
      this.gradingAssignments = [];
      this.gradingAssignmentsExamId = "";
    },

    setCategory(key) {
      if (key === this.category.key) return;
      this.categoryByType = { ...this.categoryByType, [this.examType]: key };
      this.ensureSelection();
    },

    setRound(id) {
      this.roundByScope = { ...this.roundByScope, [this.scopeKey]: id };
      this.persist();
    },

    // 특정 회차로 바로 이동 (메인 일정 카드 등): 시험 · 구분을 회차에 맞춘다
    selectRound(round) {
      this.examType = round.exam_type;
      const category = this.categories.find((c) => c.se.includes(round.exam_se));
      if (category) this.categoryByType = { ...this.categoryByType, [this.examType]: category.key };
      this.setRound(round.exam_id);
    },

    setRail(on) {
      this.rail = on;
      this.persist();
    },
  },
});
